/** @file One reconnecting WebSocket: backoff 0.5 s → 15 s, a 25 s ping, a link without an answer is dropped. */
import { parseServerFrame, type ServerFrame } from './serverFrame';

/** Client → server frames (contracts/ws-protocol.md). */
export type ClientFrame =
  | { t: 'presence'; editing: string | null }
  | { t: 'ping' }
  | { t: 'hash'; rev: number; docHash: string }
  | { t: 'renders'; before: number | null };

/** Callbacks a socket factory wires to the real socket's open, message and close events. */
export interface SocketEvents {
  open(): void;
  message(data: unknown): void;
  close(): void;
}

/** The two operations openLiveSocket needs from a socket. */
export interface SocketLike {
  send(data: string): void;
  close(): void;
}

/** Creates and connects one socket (browserSyncDeps: a real WebSocket; tests: a script). */
export type SocketFactory = (url: string, on: SocketEvents) => SocketLike;

/** setTimeout / clearTimeout, injected. */
export interface TimerLike {
  set(fn: () => void, ms: number): number;
  clear(id: number): void;
}

/** Link state reported to the owner. */
export type SocketStatus = 'connecting' | 'open' | 'closed';

/** Everything openLiveSocket needs. */
export interface LiveSocketOptions {
  url: string;
  create: SocketFactory;
  timers: TimerLike;
  onFrame(frame: ServerFrame): void;
  onStatus(status: SocketStatus): void;
}

/** `send` answers false while the link is down; `close` stops for good. */
export interface LiveSocket {
  send(frame: ClientFrame): boolean;
  close(): void;
}

const PING_MS = 25_000;
const FIRST_RETRY_MS = 500;
const MAX_RETRY_MS = 15_000;

/** The mutable state of one link; `generation` tells the current socket's events from a dropped one's. */
type Link = { socket: SocketLike | null; open: boolean; stopped: boolean; retries: number; awaitingPong: boolean; timer: number | null; generation: number };

/** Replaces the link's pending timer with `fn` after `ms`. */
const scheduleOn = (timers: TimerLike, link: Link, fn: () => void, ms: number): void => {
  if (link.timer !== null) timers.clear(link.timer);
  link.timer = timers.set(fn, ms);
};

/** Sends while the link is open; false otherwise. */
const sendOn = (link: Link, frame: ClientFrame): boolean => {
  if (!link.open || link.socket === null) return false;
  link.socket.send(JSON.stringify(frame));
  return true;
};

/** Connects to `opts.url` now and keeps the link up until `close()` (E8-R8). */
export function openLiveSocket(opts: LiveSocketOptions): LiveSocket {
  const link: Link = { socket: null, open: false, stopped: false, retries: 0, awaitingPong: false, timer: null, generation: 0 };
  const schedule = (fn: () => void, ms: number): void => { scheduleOn(opts.timers, link, fn, ms); };
  const send = (frame: ClientFrame): boolean => sendOn(link, frame);
  /** The current socket is gone: ignore its late events and schedule the next attempt. */
  const lost = (): void => {
    Object.assign(link, { generation: link.generation + 1, open: false, socket: null });
    opts.onStatus('closed');
    if (!link.stopped) schedule(connect, Math.min(MAX_RETRY_MS, FIRST_RETRY_MS * 2 ** link.retries++));
  };
  const ping = (): void => {
    const dead = link.awaitingPong ? link.socket : null;
    if (dead !== null) {
      lost();
      dead.close();
      return;
    }
    link.awaitingPong = send({ t: 'ping' });
    schedule(ping, PING_MS);
  };
  function connect(): void {
    const generation = ++link.generation;
    const mine = (): boolean => generation === link.generation;
    opts.onStatus('connecting');
    link.socket = opts.create(opts.url, {
      open: () => {
        if (!mine()) return;
        Object.assign(link, { open: true, retries: 0, awaitingPong: false });
        opts.onStatus('open');
        schedule(ping, PING_MS);
      },
      message: (data) => {
        if (!mine()) return;
        link.awaitingPong = false;
        const frame = typeof data === 'string' ? parseServerFrame(data) : null;
        if (frame !== null) opts.onFrame(frame);
      },
      close: () => { if (mine()) lost(); },
    });
  }
  connect();
  const close = (): void => {
    link.stopped = true;
    if (link.timer !== null) opts.timers.clear(link.timer);
    const socket = link.socket;
    lost();
    socket?.close();
  };
  return { send, close };
}
